import type {MaterialStorage} from '@domain/material-storage'
import {Asset, UpmDepartmentOrEquipmentType} from '@hconnect/common/types'
import {dataTestId} from '@hconnect/uikit'
import {Alert} from '@hconnect/uikit/src/lib2'
import {SettingsBackupRestoreOutlined as SettingsBackupRestoreOutlinedIcon} from '@mui/icons-material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import {LoadingButton, LoadingButtonProps} from '@mui/lab'
import {Button, Stack, Typography} from '@mui/material'
import {useTranslation} from 'react-i18next'

import {isDepartmentOrEquipmentNode} from '../../helpers'
import {useSelectedItem} from '../../providers'

import {AssetPlanningMenu} from './AssetPlanningMenu'
import {ItemDetailsLayoutHeaderSaveBtn} from './ItemDetailsLayoutHeaderSaveBtn'
import {StoragePlanningMenu} from './StoragePlanningMenu'

type ItemDetailsButtonShow = {
  onClick: () => void
  hide: false
  disabled?: boolean
  ButtonProps?: Omit<LoadingButtonProps, 'variant' | 'startIcon' | 'onClick' | 'disabled'>
}

type ItemDetailsButtonHide = {
  hide: true
}

export type ItemDetailsButtonProps = ItemDetailsButtonShow | ItemDetailsButtonHide

interface ItemDetailsLayoutHeaderProps {
  title?: string
  titleAction?: {
    label: string
    onClick: () => void
  }
  revertButton?: ItemDetailsButtonProps
  saveButton?: ItemDetailsButtonProps
}

// eslint-disable-next-line complexity
export const ItemDetailsLayoutHeader: React.FC<ItemDetailsLayoutHeaderProps> = ({
  title,
  titleAction,
  revertButton,
  saveButton
}) => {
  const {t} = useTranslation()

  const {selectedItem} = useSelectedItem()

  const canItemBeEnabledForPlanning = isDepartmentOrEquipmentNode(selectedItem)

  const shouldDisplayEnableForPlanningWarning =
    canItemBeEnabledForPlanning && !selectedItem.enabledForPlanningItem

  const shouldRevertButtonBeDisplayed = revertButton && !revertButton.hide
  const shouldSaveButtonBeDisplayed = saveButton && !saveButton.hide

  return (
    <Stack spacing={3}>
      <Stack flexDirection="row" justifyContent="space-between">
        <Stack>
          {titleAction && (
            <Button
              onClick={titleAction.onClick}
              startIcon={<ChevronLeftIcon />}
              variant="text"
              sx={{fontWeight: 500, p: 0.5, pl: 0, justifyContent: 'flex-start', display: 'flex'}}
              size="small"
            >
              {titleAction.label}
            </Button>
          )}
          <Typography variant="h3" {...dataTestId('janus-structure-details-title')}>
            {title ?? selectedItem?.text}
          </Typography>
          <Typography variant="body2" {...dataTestId('janus-structure-details-sub-title')}>
            {Number(selectedItem?.level) > 1 &&
              selectedItem?.type?.name &&
              selectedItem?.type?.name !== 'N/A' &&
              selectedItem?.type?.name}
          </Typography>
        </Stack>
        <Stack flexDirection="row" alignItems="flex-start" gap={1}>
          {shouldRevertButtonBeDisplayed && (
            <LoadingButton
              {...dataTestId('plant-structure-details-revert-button')}
              variant="outlined"
              startIcon={<SettingsBackupRestoreOutlinedIcon />}
              onClick={revertButton.onClick}
              disabled={revertButton.disabled || !revertButton?.onClick}
              {...revertButton.ButtonProps}
            >
              {t('plantSetup.action.revert')}
            </LoadingButton>
          )}
          {shouldSaveButtonBeDisplayed && (
            <ItemDetailsLayoutHeaderSaveBtn
              onClick={saveButton.onClick}
              disabled={saveButton.disabled || !saveButton?.onClick}
              buttonProps={saveButton.ButtonProps}
            />
          )}
          {canItemBeEnabledForPlanning &&
            (selectedItem.departmentOrEquipmentType === UpmDepartmentOrEquipmentType.Asset ? (
              <AssetPlanningMenu asset={selectedItem.enabledForPlanningItem as Asset | undefined} />
            ) : (
              <StoragePlanningMenu
                storage={selectedItem.enabledForPlanningItem as MaterialStorage | undefined}
              />
            ))}
        </Stack>
      </Stack>
      {shouldDisplayEnableForPlanningWarning && (
        <Alert
          accentColor="warning"
          content={t('plantSetup.enableForPlanningWarning', {
            item: t(`plantSetup.${selectedItem.departmentOrEquipmentType}`)
          })}
          {...dataTestId('janus-structure-details-enable-for-planning-warning')}
        />
      )}
    </Stack>
  )
}
